/**
 * A finished turn, rebuilt from what the server kept.
 *
 * `PlanningChatMessage.parts` carries 19 part types and the first pass of this chat read exactly one
 * of them, `content`. The consequence was not subtle: a turn whose substance was six tool rows and a
 * document proposal came back after a reload as an empty bubble. The live path and the reload path
 * have to produce the same `TurnBlock[]` or the transcript is a different thing depending on whether
 * you refreshed.
 *
 * Every payload is JSON stringified into `MessagePart.content`, so every read here is a `try`. A part
 * that will not parse is skipped rather than thrown — one malformed row from an old backend version
 * should cost its own line, not the whole conversation.
 *
 * The live reasoning stream becomes a settled `THINKING_CHECKPOINT` on the server. While a response is
 * still settling, the v1.5 cache also carries `DECK15_BLOCK` snapshots so clearing the live overlay is
 * lossless. Artifact cards are a separate concern: their slide ids are still owned by the canvas.
 */
import type { AttachmentCardItem } from '~/design-system/components/PlanningChat/ui/AttachmentCardsGridUI';
import { splitFilename } from '~/design-system/components/PlanningChat/ui/AttachmentCardsGridUI';
import type { AgentLifecycle, ArtifactReferencePin, ItemState } from '~/design-system/components/v1_5/Chat';
import type { MessagePart, PlanningChatMessage } from '~/types/planning';
import { getDocumentSelectionChipLabel } from '~/utils/documentSelection';
import {
  extractCanvasAttachmentCards,
  parseContextRequestCompletionContent,
  stripSystemContext,
} from '~/utils/planning-utils';
import { annotationMark, annotationsBlockSlideId, parseAnnotationsBlock } from '../annotate/withAnnotations';
import { DECK15_BLOCK_PART_TYPE, type TurnBlock, type TurnListRow } from './turnModel';
import { toolFromLabel } from './wireVocabulary';
import { readableContent, readablePlanningContent } from './workspaceContent';

interface ThinkingCheckpoint {
  text?: string;
  durationSec?: number;
}
interface SearchPayload {
  query?: string;
  results?: { title?: string }[];
}
interface UrlPayload {
  url?: string;
  title?: string;
  ok?: boolean;
  errorMessage?: string;
}
interface DocumentPayload {
  documentId?: string;
  title?: string;
}

/* ── the five payloads that used to render as bare headers ──────────────────────────────────── */

interface LinearPayload {
  mode?: 'search' | 'read';
  issues?: { identifier?: string; title?: string }[];
}
interface GithubPayload {
  mode?: 'commits' | 'prs';
  repo?: string;
  items?: { sha?: string; message?: string; number?: string; title?: string; state?: string }[];
}
interface GoalPayload {
  phase?: string;
  goalText?: string;
  text?: string;
  previousGoalText?: string;
}
interface OutlinePayload {
  chapters?: {
    name?: string;
    title?: string;
    canvases?: { name?: string; title?: string; status?: string }[];
  }[];
}
/**
 * The kit dispatch payload, replayed from the KIT_UPDATE part after a reload. It is byte-identical to
 * the SSE event, which is the point: the row a reloaded thread draws has to be the row the live turn
 * drew, keyed on the same `requestId` so it lands once and not twice.
 */
interface KitPayload {
  kitId?: string;
  requestId?: string;
  section?: string | null;
  operation?: string;
  instruction?: string | null;
  source?: string;
}

interface CanvasPayload {
  status?: string;
  operation?: 'edit' | 'generate';
  chapterName?: string;
  canvasName?: string;
  canvasText?: string;
  headerLabel?: string;
  canvases?: { name?: string; title?: string; chapter?: string; status?: string }[];
  completedCount?: number;
  totalCount?: number;
  /** IDs are persisted for rewrites so artifact cards can be rebuilt after a reload. */
  slideIds?: number[];
}

function read<T>(part: MessagePart): T | null {
  try {
    return JSON.parse(part.content) as T;
  } catch {
    return null;
  }
}

/** `WEB_SEARCH_CALL` → one research card. Same shape the live handler builds, so they render alike. */
function searchBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = read<SearchPayload>(part);
  if (!payload) return null;
  const rows: TurnListRow[] = (payload.results ?? []).map((hit) => ({
    state: 'done' as const,
    label: hit.title ?? '',
  }));
  return {
    kind: 'activity',
    id,
    tool: 'research',
    label: 'Searched the web',
    meta: `${rows.length} source${rows.length === 1 ? '' : 's'}`,
    lifecycle: 'complete',
    body: { kind: 'list', rows },
  };
}

function urlBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = read<UrlPayload>(part);
  if (!payload) return null;
  const name = payload.title || payload.url || '';
  return {
    kind: 'activity',
    id,
    tool: 'research',
    label: payload.ok === false ? `Could not read ${name}` : `Read ${name}`,
    lifecycle: payload.ok === false ? 'failed' : 'complete',
    body:
      payload.ok === false && payload.errorMessage
        ? { kind: 'explanation', text: payload.errorMessage }
        : { kind: 'none' },
  };
}

function documentBlock(part: MessagePart, id: string, verb: string, deleted = false): TurnBlock | null {
  const payload = read<DocumentPayload>(part);
  if (!payload) return null;
  return {
    kind: 'activity',
    id,
    tool: 'document',
    label: `${verb} ${payload.title ?? 'a document'}`,
    ...(!deleted && payload.documentId ? { resourceKey: `document:${payload.documentId}`, resourceTitle: payload.title } : {}),
    lifecycle: deleted ? 'deleted' : 'complete',
    body: { kind: 'none' },
  };
}

/**
 * `n` of a thing, with the singular that survives review.
 *
 * The plural is a parameter and not an `+ 's'`, because the one noun this file counts most is
 * `canvas`.
 */
function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * `LINEAR_READ` → the issues it read, as rows.
 *
 * The payload is the same one `LinearWidgetUI` takes on the shipping chat (`{ mode, issues }`), and
 * `ActivityListRow` has a slot the shipping widget's row does not: `itemKey`, the 500-weight run
 * that holds `DECK-482` in a readable column down the left of the card. The identifier goes there
 * rather than being concatenated into the label.
 */
function linearBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = read<LinearPayload>(part);
  if (!payload) return null;
  const issues = payload.issues ?? [];
  const rows: TurnListRow[] = issues.map((issue) => ({
    state: 'done' as ItemState,
    itemKey: issue.identifier || undefined,
    label: issue.title || issue.identifier || '',
  }));
  return {
    kind: 'activity',
    id,
    tool: 'linear',
    label: payload.mode === 'search' ? 'Searched Linear' : 'Read Linear issues',
    meta: rows.length > 0 ? count(rows.length, 'issue') : undefined,
    lifecycle: 'complete',
    body: rows.length > 0 ? { kind: 'list', rows } : { kind: 'none' },
  };
}

/**
 * `GITHUB_READ` → the commits or pull requests it read.
 *
 * Two shapes in one part type: a commit row carries `sha` + `message`, a PR row carries `number` +
 * `title`. Both collapse onto the same `itemKey` / `label` pair, which is what lets one card draw
 * both — the SHA and the `#412` are the same column.
 */
function githubBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = read<GithubPayload>(part);
  if (!payload) return null;
  const prs = payload.mode === 'prs';
  const items = payload.items ?? [];
  const rows: TurnListRow[] = items.map((item) => ({
    state: 'done' as ItemState,
    itemKey: prs
      ? item.number
        ? `#${item.number}`
        : undefined
      : item.sha
        ? item.sha.slice(0, 7)
        : undefined,
    label: item.title || item.message || '',
  }));
  return {
    kind: 'activity',
    id,
    tool: 'github',
    label: prs ? 'Read pull requests' : 'Read commits',
    // The repo when it is known, the count when it is not. Both are the one thing a reader wants
    // from a settled row they are scanning past.
    meta: payload.repo || (rows.length > 0 ? count(rows.length, prs ? 'pull request' : 'commit') : undefined),
    lifecycle: 'complete',
    body: rows.length > 0 ? { kind: 'list', rows } : { kind: 'none' },
  };
}

/** The four goal/canvas phases that persist, onto the lifecycle the design draws. */
function lifecycleOf(phase: string | undefined): AgentLifecycle {
  switch (phase) {
    case 'writing':
      return 'running';
    case 'updating':
      return 'updating';
    case 'accepted':
      return 'accepted';
    case 'rejected':
      return 'rejected';
    default:
      return 'complete';
  }
}

/** A goal part is either a JSON payload or, on older messages, the goal itself as plain text. */
function goalPayload(part: MessagePart): GoalPayload {
  const raw = part.content.trim();
  if (!raw.startsWith('{')) return { goalText: readableContent(raw) };
  return read<GoalPayload>(part) ?? { goalText: raw };
}

/**
 * `GOAL_UPDATE` → the goal it wrote, as the card's explanation body.
 *
 * The goal is prose the agent committed to, so it belongs in the body rather than in the header's
 * meta: a one-line meta would truncate the sentence the whole turn was about.
 */
function goalBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = goalPayload(part);
  const text = readablePlanningContent(payload.goalText ?? payload.text).trim();
  return {
    kind: 'activity',
    id,
    tool: 'goal',
    resourceKey: 'goal',
    label: payload.previousGoalText ? 'Updated the goal' : 'Wrote the goal',
    lifecycle: lifecycleOf(payload.phase),
    body: text ? { kind: 'explanation', text } : { kind: 'none' },
  };
}

/**
 * `OUTLINE_UPDATE` → one row per chapter, with how much of it is written.
 *
 * Chapters, not canvases. An outline for a real deck is fifteen-odd canvases across four chapters
 * and a flat list of fifteen rows in a 400px column is a scroll, not a summary — the chapter is the
 * unit the user named, and the `4/4` beside it is the part they are checking.
 */
function outlineBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = read<OutlinePayload>(part);
  if (!payload) return null;
  const chapters = payload.chapters ?? [];
  let canvases = 0;
  const rows: TurnListRow[] = chapters.map((chapter) => {
    const children = chapter.canvases ?? [];
    canvases += children.length;
    // Older saved outline events left the last successfully written chapter marked "active".
    // These persisted entries describe saved outline text, not artifact generation in flight.
    const done = children.filter((canvas) => canvas.status === 'complete' || canvas.status === 'active').length;
    return {
      state: (children.length > 0 && done === children.length
        ? 'done'
        : done > 0
          ? 'running'
          : 'pending') as ItemState,
      label: chapter.name || chapter.title || 'Chapter',
      ...(children.length > 0 ? { itemKey: `${done}/${children.length}` } : {}),
    };
  });
  return {
    kind: 'activity',
    id,
    tool: 'outline',
    resourceKey: 'outline',
    label: 'Built the outline',
    meta: canvases > 0 ? count(canvases, 'artifact', 'artifacts') : undefined,
    lifecycle: 'complete',
    body: rows.length > 0 ? { kind: 'list', rows } : { kind: 'none' },
  };
}

/**
 * `CANVAS_UPDATE` → which artifacts the turn wrote, and how far it got.
 *
 * A batch run names every canvas it is walking; a single edit names one and carries the text it
 * wrote. The two are the same part type, so the reader takes whichever is there — the list when
 * there is one, the written text as an explanation when there is not.
 */
function canvasBlock(part: MessagePart, id: string): TurnBlock | null {
  const raw = part.content.trim();
  // Older parts carry the canvas name as a bare string.
  const payload: CanvasPayload = raw.startsWith('{') ? read<CanvasPayload>(part) ?? {} : { canvasName: raw };
  const canvases = payload.canvases ?? [];
  const rows: TurnListRow[] = canvases.map((canvas) => ({
    state: (canvas.status === 'complete'
      ? 'done'
      : canvas.status === 'writing' || canvas.status === 'running'
        ? 'running'
        : 'pending') as ItemState,
    label: canvas.title || canvas.name || 'Artifact',
  }));

  const done = payload.completedCount;
  const all = payload.totalCount ?? (rows.length > 0 ? rows.length : undefined);

  return {
    kind: 'activity',
    id,
    tool: payload.operation === 'generate' ? 'artifact' : 'canvas-edit',
    label: payload.headerLabel || payload.canvasName || 'Edited the artifact',
    meta:
      all != null && done != null
        ? `${done}/${all}`
        : payload.chapterName || (all != null ? count(all, 'artifact') : undefined),
    lifecycle: lifecycleOf(payload.status),
    body:
      rows.length > 0
        ? { kind: 'list', rows }
        : payload.canvasText?.trim()
          ? { kind: 'explanation', text: payload.canvasText.trim() }
          : { kind: 'none' },
  };
}

/**
 * A settled brand kit change.
 *
 * It reads as COMPLETE and never as running: this is a reload, so whatever the browser was going to do
 * with the dispatch either happened or did not, and a spinner restored from history would be a spinner
 * nothing is driving. The kit page shows the truth of its own state; this row records that the agent
 * asked.
 */
function kitBlock(part: MessagePart, id: string): TurnBlock | null {
  const payload = read<KitPayload>(part) ?? {};
  const setup = Boolean(payload.source) && !payload.section;
  const label = payload.instruction?.trim()
    || (setup ? 'Set up a brand kit' : 'Updated the brand kit');
  return {
    kind: 'activity',
    id,
    tool: 'brand-kit',
    label,
    meta: setup ? payload.source : (payload.section ?? undefined),
    lifecycle: 'complete',
    body: { kind: 'none' },
  };
}

/**
 * The goal this turn overrode, if it overrode one.
 *
 * `14I1-0` (*Decision superseded*) — "an earlier call the later one overrode. it stays, or the
 * thread lies." The only place the product actually keeps an overridden decision is
 * `GOAL_UPDATE.previousGoalText`, written when the phase is `updating`, and it is dropped on every
 * surface today. Returned separately from the block list rather than as a block, because the
 * artboard puts the quote ABOVE the user message that caused it — it is not part of the reply, it
 * is what the reply replaced.
 */
export function supersededFromMessage(message: PlanningChatMessage): string | null {
  for (const part of message.parts ?? []) {
    if (part.type !== 'GOAL_UPDATE') continue;
    const previous = goalPayload(part).previousGoalText?.trim();
    if (previous) return previous;
  }
  return null;
}

/**
 * One persisted message → the blocks it should draw.
 *
 * Order is the array's order, which is the order the server wrote them in, which is the order they
 * happened. That is the whole reason parts exist rather than one concatenated string.
 */
export function blocksFromMessage(
  message: PlanningChatMessage,
  opts: {
    /**
     * The message is the paused half of a turn that is being RESUMED right now, so its incompleteness is
     * the pause, not a failure. Guri, 2026-09-05: "the response interrupted works every time I answer the
     * question". A turn that stops to ask is stored incomplete while it waits; the answer resumes the same
     * message, and the settled half is rebuilt from that stored copy with the question already marked
     * answered, which read here as "answered, then died". After a reload the same shape IS a death (the
     * resume never finished), so the marker stays for that reading; only the live resume drops it.
     */
    resuming?: boolean;
  } = {},
): TurnBlock[] {
  const parts = message.parts ?? [];

  // No parts at all is the ordinary case for an old message, and for every user message. Fall back to
  // the prose, which is what the first pass read exclusively.
  if (parts.length === 0 && !(message.pendingContextRequests ?? []).some((request) => request.status !== 'COMPLETED')) {
    const text = message.content?.trim();
    // `resuming` holds here too. A turn that is still running (a resume, or one this page attached to
    // after a reload) owns an incomplete row with nothing durable in it yet; that is the start of the
    // turn, not its death, so it draws nothing and the caller's working mark says the rest.
    const dead = message.role === 'assistant' && message.isComplete === false && !opts.resuming;
    if (text) return [
      { kind: 'prose', id: `${message.id}-prose`, text },
      ...(dead ? interruptedResponse(message.id, 'The response was interrupted before the agent finished. Please try again.') : []),
    ];
    // The backend keeps an incomplete assistant placeholder when a stream is interrupted before its
    // first durable token. Keep that turn visible instead of making a refresh erase the user's action.
    if (dead) {
      return interruptedResponse(message.id, 'Generation was interrupted before a response arrived. Please try again.');
    }
    return [];
  }

  const blocks: TurnBlock[] = [];
  const persistedArtifactIds = new Set<number>();
  /** Where the newest snapshot of a self-superseding part type already sits. See its case below. */
  const latest = new Map<string, number>();

  parts.forEach((part, index) => {
    const id = `${message.id}-p${index}`;

    switch (part.type) {
      case 'ERROR':
        blocks.push(...interruptedResponse(id, part.content.trim() || 'The response was interrupted. Please try again.'));
        break;
      case 'TEXT': {
        if (part.content.trim()) blocks.push({ kind: 'prose', id, text: part.content });
        break;
      }

      case 'THINKING_CHECKPOINT': {
        const payload = read<ThinkingCheckpoint>(part);
        if (payload?.text?.trim()) {
          blocks.push({
            kind: 'thinking',
            id,
            // The recap renders as plain text, so the markdown the model emitted while reasoning is
            // stripped rather than rendered half-heartedly. Same treatment the shipping chat gives it.
            text: payload.text.replace(/\*\*/g, '').replace(/^#+\s*/gm, ''),
            durationSec: payload.durationSec,
            live: false,
          });
        }
        break;
      }

      case DECK15_BLOCK_PART_TYPE: {
        const block = read<TurnBlock>(part);
        // Prose has its own TEXT representation. All other blocks are already the exact shape the
        // v1.5 renderer consumes, so keep their order and state instead of reducing them to a label.
        if (block && block.kind !== 'prose') blocks.push({ ...block, id });
        break;
      }

      case 'ACTION':
      case 'ACTION_THINKING': {
        if (part.content.trim()) {
          // A finished tool step is persisted as its final wording alone; the prefix names the tool.
          blocks.push({
            kind: 'activity',
            id,
            tool: toolFromLabel(part.content),
            label: part.content,
            lifecycle: 'complete',
            body: { kind: 'none' },
          });
        }
        break;
      }

      case 'WEB_SEARCH_CALL': {
        const block = searchBlock(part, id);
        if (block) blocks.push(block);
        break;
      }
      case 'WEB_SCRAPE_CALL':
      case 'URL_CONTEXT': {
        const block = urlBlock(part, id);
        if (block) blocks.push(block);
        break;
      }

      case 'DOCUMENT_PROPOSAL': {
        const block = documentBlock(part, id, 'Wrote');
        if (block) blocks.push(block);
        break;
      }
      case 'DOCUMENT_UPDATED': {
        const block = documentBlock(part, id, 'Updated');
        if (block) blocks.push(block);
        break;
      }
      case 'DOCUMENT_DELETED': {
        const block = documentBlock(part, id, 'Removed', true);
        if (block) blocks.push(block);
        break;
      }

      /**
       * The five that used to be bare headers.
       *
       * Each payload is its own shape, which is why they were stubbed: there is no generic reader
       * for "an update". Now there are five, and they are deduped the way the live stream deduped
       * them — the agent sends a tool's progress as a series of whole SNAPSHOTS, not deltas, so an
       * outline that grew over eleven events persists as eleven `OUTLINE_UPDATE` parts and only the
       * last one is true. Appending all of them stacked eleven cards for one outline.
       */
      case 'LINEAR_READ':
      case 'GITHUB_READ':
      case 'GOAL_UPDATE':
      case 'OUTLINE_UPDATE':
      case 'CANVAS_UPDATE': {
        const canvasPayload = read<CanvasPayload>(part);
        canvasPayload?.slideIds?.forEach((slideId) => persistedArtifactIds.add(slideId));
        const block =
          part.type === 'LINEAR_READ' ? linearBlock(part, id)
          : part.type === 'GITHUB_READ' ? githubBlock(part, id)
          : part.type === 'GOAL_UPDATE' ? goalBlock(part, id)
          : part.type === 'OUTLINE_UPDATE' ? outlineBlock(part, id)
          : canvasBlock(part, id);
        if (!block) break;
        // Keyed by part type rather than by id, which is what makes the last snapshot win IN PLACE:
        // the card stays where the first one landed, so the turn's order still reads as what
        // happened, and the content is the finished state.
        const at = latest.get(part.type);
        if (at === undefined) {
          latest.set(part.type, blocks.length);
          blocks.push(block);
        } else {
          blocks[at] = { ...block, id: (blocks[at] as { id: string }).id };
        }
        break;
      }

      case 'KIT_UPDATE': {
        const block = kitBlock(part, id);
        if (!block) break;
        // Keyed on the request rather than the part type, which is the opposite of CANVAS_UPDATE above
        // and deliberately so: those parts are successive snapshots of one canvas job, while two kit
        // edits in a turn are two separate changes and collapsing them would hide one.
        const kitKey = `KIT_UPDATE:${read<KitPayload>(part)?.requestId ?? id}`;
        const kitAt = latest.get(kitKey);
        if (kitAt === undefined) {
          latest.set(kitKey, blocks.length);
          blocks.push(block);
        } else {
          blocks[kitAt] = { ...block, id: (blocks[kitAt] as { id: string }).id };
        }
        break;
      }

      case 'CONTEXT_COMPLETED':
      case 'CONTEXT_SKIPPED': {
        // The marker names a request; the answers live on the request itself, which persists on the
        // message. Matching them back by id is what makes an answered question survive a reload.
        const { requestId } = parseContextRequestCompletionContent(part.content);
        const request = (message.pendingContextRequests ?? []).find((r) => r.requestId === requestId);
        blocks.push({
          kind: 'question',
          id,
          requestId: requestId ?? id,
          items: request?.clarificationData?.items ?? [],
          status: part.type === 'CONTEXT_SKIPPED' ? 'skipped' : 'answered',
        });
        break;
      }

      // `OUTLINE_PLAN` and `PLANNING_TASKS` render nothing once settled, by design — they are live
      // beats. `ACTION_CASCADE` has no SSE producer at all and appears only in the demo scenarios.
      case 'OUTLINE_PLAN':
      case 'PLANNING_TASKS':
      case 'ACTION_CASCADE':
      default:
        break;
    }
  });

  /**
   * A question that was asked and never answered leaves no marker part, so it is not in the loop
   * above — but it is still pending and the composer still owes the user a card. Append it.
   */
  (message.pendingContextRequests ?? [])
    .filter((request) => request.status !== 'COMPLETED')
    .forEach((request) => {
      if (blocks.some((block) => block.kind === 'question' && block.requestId === request.requestId)) return;
      blocks.push({
        kind: 'question',
        id: `${message.id}-q-${request.requestId}`,
        requestId: request.requestId,
        items: request.clarificationData?.items ?? [],
        status: 'asking',
      });
  });

  // The live dispatch creates artifact cards from its event. Once the stream is gone, the event is
  // represented by CANVAS_UPDATE instead; append the same cards from its persisted slide IDs so a
  // reload does not turn a generated artifact back into an unclickable activity row.
  persistedArtifactIds.forEach((slideId) => {
    if (blocks.some((block) => block.kind === 'artifact' && block.slideId === slideId)) return;
    blocks.push({ kind: 'artifact', id: `${message.id}-artifact-${slideId}`, slideId });
  });

  // A saved thinking/text checkpoint is not a completed response. Legacy failed turns predate
  // durable ERROR parts; keep their partial history, but make the interrupted outcome explicit.
  if (message.role === 'assistant' && message.isComplete === false && !opts.resuming &&
      !blocks.some((block) => (block.kind === 'activity' && block.lifecycle === 'failed') ||
        (block.kind === 'question' && block.status === 'asking'))) {
    blocks.push(...interruptedResponse(message.id, 'The response was interrupted before the agent finished. Please try again.'));
  }

  return blocks;
}

function interruptedResponse(id: string, reason: string): TurnBlock[] {
  return [
    { kind: 'activity', id: `${id}-error`, tool: 'clock', label: 'Response interrupted', lifecycle: 'failed', body: { kind: 'none' } },
    { kind: 'prose', id: `${id}-error-text`, text: reason },
  ];
}

/* ── a turn still running on the server ─────────────────────────────────────────────────────── */

/** The same shape as `DeckChat['live']`, so an attached turn and a streamed one render through one path. */
export interface RunningTurn {
  messageId: string;
  blocks: TurnBlock[];
  elapsedSec: number;
  thinking: boolean;
}

const PENDING_REPLY_SUFFIX = ':pending-reply';

/** The id `runningTurn` gives the reply the server has not written a row for yet. No server id looks like it. */
export function pendingReplyId(userMessageId: string): string {
  return `${userMessageId}${PENDING_REPLY_SUFFIX}`;
}

function epochMs(at: Date | string | undefined): number {
  const ms = at instanceof Date ? at.getTime() : new Date(at ?? NaN).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

/** Does anything in these blocks already draw a working mark of its own in `DeckThread`? */
function ownsWorkingMark(blocks: TurnBlock[]): boolean {
  return blocks.some((block) =>
    (block.kind === 'thinking' && block.live) ||
    (block.kind === 'activity' && (block.lifecycle === 'running' || block.lifecycle === 'updating')) ||
    (block.kind === 'task' && block.state === 'running') ||
    (block.kind === 'question' && block.status === 'asking'));
}

/**
 * The turn the server is still running for this conversation, rebuilt from its checkpoints.
 *
 * A reload (or a detached reader) used to leave the last assistant row incomplete on screen, which
 * `blocksFromMessage` reads as a death: "Response interrupted" under a turn that was in fact still
 * working, with an idle composer inviting a second send. The history now says `isRunning` on the last
 * message, and this is what the pane draws while it polls: null unless that flag is set.
 *
 * - **Assistant last.** Its parts rebuilt with `resuming` (checkpoints are the turn so far, not a
 *   failure), then made to END in a working mark, because `DeckThread` draws no indicator under a
 *   prose tail (words arriving are their own liveness signal while streaming, but a poll delivers them
 *   in lumps and a reader cannot tell a finished paragraph from a paused one). A tail thinking
 *   checkpoint IS the reasoning in progress, so it goes live with its text rolling; anything else gets
 *   an empty live thinking block after it. Nothing is added when a block already draws its own mark.
 * - **User last.** The turn has started and the server has not written the assistant row yet (context,
 *   image analysis). The reply is represented by `pendingReplyId(user.id)` with a single live thinking
 *   block, and `withPendingReply` puts a matching placeholder row in the list the thread renders, so
 *   the Thinking row sits inside that turn with the true clock. Returning null here would fall back to
 *   `LiveThinkingFooter`, whose clock restarts at 0:00 on attach instead of counting from the send.
 *
 * `elapsedSec` counts from the server's `runningSince` (the moment it took the turn) when the row carries
 * one. A retry keeps yesterday's user row, so that row is no clock. Without it, the count falls back to
 * the preceding user message (the send), else to the row itself.
 * `thinking` is true when the returned blocks end in a live thinking block.
 */
export function runningTurn(messages: readonly PlanningChatMessage[], now: number): RunningTurn | null {
  const last = messages[messages.length - 1];
  if (!last?.isRunning) return null;
  // The contract never flags a finished row; if one arrives flagged anyway, the row wins.
  if (last.role === 'assistant' && last.isComplete === true) return null;

  let anchor = last.role === 'user' ? last : undefined;
  for (let i = messages.length - 2; !anchor && i >= 0; i -= 1) {
    if (messages[i].role === 'user') anchor = messages[i];
  }
  const claimedAt = epochMs(last.runningSince ?? undefined);
  const startedAt = Number.isFinite(claimedAt) ? claimedAt : epochMs((anchor ?? last).createdAt);
  const elapsedSec = Number.isFinite(startedAt) ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;

  const messageId = last.role === 'user' ? pendingReplyId(last.id) : last.id;
  const rebuilt = last.role === 'assistant' ? blocksFromMessage(last, { resuming: true }) : [];
  let blocks = rebuilt;
  if (!ownsWorkingMark(rebuilt)) {
    const tail = rebuilt[rebuilt.length - 1];
    blocks = tail?.kind === 'thinking'
      ? [...rebuilt.slice(0, -1), { ...tail, live: true }]
      : [...rebuilt, { kind: 'thinking', id: `${messageId}-working`, text: '', live: true }];
  }
  const tail = blocks[blocks.length - 1];
  return { messageId, blocks, elapsedSec, thinking: tail?.kind === 'thinking' && tail.live };
}

/**
 * The list the thread renders while a running turn has no assistant row yet: the messages plus one
 * empty, incomplete placeholder under `runningTurn`'s pending id. Render-only, never written to the
 * shared cache. Returns `messages` itself when there is nothing to add, so identity holds.
 */
export function withPendingReply(
  messages: PlanningChatMessage[],
  liveMessageId: string | null | undefined,
): PlanningChatMessage[] {
  if (!liveMessageId?.endsWith(PENDING_REPLY_SUFFIX)) return messages;
  if (messages.some((message) => message.id === liveMessageId)) return messages;
  const user = messages[messages.length - 1];
  if (!user || user.role !== 'user' || pendingReplyId(user.id) !== liveMessageId) return messages;
  return [
    ...messages,
    {
      id: liveMessageId,
      role: 'assistant',
      content: '',
      createdAt: user.createdAt,
      actions: [],
      parts: [],
      pendingContextRequests: null,
      isComplete: false,
      isRunning: true,
    },
  ];
}

/* ── the user's half ────────────────────────────────────────────────────────────────────────── */

/** One chip above a user bubble. `AttachmentCard variant="titled"` takes exactly these two strings. */
export interface DecorationCard {
  id: string;
  /** The 11px label — `Slide`, `Doc`, `Image`, `PDF`, `Goal`. Free text; Paper ships four of them. */
  kind: string;
  /** Truncates to one line in the 76px column. That truncation is the design. */
  name: string;
}

/** Everything a user message carries that is not its sentence. */
export interface MessageDecorations {
  /** Screenshot URLs — `MessageImageGrid` + `ImageThumb size="grid"`, the *Many screenshots* board. */
  images: string[];
  /** `MessageAttachmentRow` + `AttachmentCard`, the *With attachments* board. */
  cards: DecorationCard[];
}

const NO_DECORATIONS: MessageDecorations = { images: [], cards: [] };

/**
 * The one agent-only marker `stripSystemContext` does not know about.
 *
 * `[Artifact focused: slide 14 ("…") — of 14 artifact slot(s) on this page, style: "…" brand kit]`
 * is written by `ArtifactAgent`'s composer onto every outgoing prompt so the agent knows which slot
 * "it" means. It is the same class of thing as `[Selected on canvas: Slide 4]` and it is persisted
 * the same way — but it postdates `planning-utils`, so it is not in that function's list and it was
 * showing up verbatim at the top of real user bubbles in this thread.
 *
 * `[Artifact style: "…" brand kit]` is the same prefix's no-slot form and goes with it.
 */
const ARTIFACT_MARKER = /\[Artifact (?:focused|style):[^\]]*\]/g;

/**
 * The user's message, with the agent's half of it removed.
 *
 * Everything `stripSystemContext` strips, plus the marker above. Used for the bubble AND for copy —
 * a Copy that pasted `[Artifact focused: slide 14 …]` into somebody's notes would be pasting a
 * sentence the user never wrote.
 */
export function visiblePrompt(content: string): string {
  return stripSystemContext((content ?? '').replace(ARTIFACT_MARKER, '')).trim();
}

/**
 * What rode above a user bubble, rebuilt from what the server kept.
 *
 * Four fields persist on a user message and the first pass of this chat read none of them: `images`,
 * `files`, `attachmentCards` and `documentTextSelection`. A turn that was "fix these six screenshots"
 * came back as the words alone, which is the same failure the assistant side had — the substance
 * survived the reload and the renderer dropped it.
 *
 * A fifth source is not a field at all: the agent-only markers in the prompt text
 * (`[Selected on canvas: Slide 4]`). Those are what the backend actually persists for a canvas
 * attachment — `attachmentCards` is optimistic state that dies with the tab — so
 * `extractCanvasAttachmentCards` is the reload path for them, and `stripSystemContext` is why the
 * bubble never shows the raw marker.
 *
 * Deduped by kind+name across all five, because a message sent in this session has BOTH its
 * optimistic cards and its markers and would otherwise show every chip twice.
 */
export function decorationsFromMessage(message: PlanningChatMessage): MessageDecorations {
  if (message.role !== 'user') return NO_DECORATIONS;

  const images: string[] = [...(message.images ?? [])];
  const cards: DecorationCard[] = [];
  const seen = new Set<string>();

  const add = (kind: string, name: string, id: string) => {
    const key = `${kind}:${name}`.toLowerCase();
    if (!name || seen.has(key)) return;
    seen.add(key);
    cards.push({ id, kind, name });
  };

  // Optimistic cards from this session. An image card goes to the GRID rather than the row: a
  // screenshot is a picture, and *Many screenshots* is the board that draws six of them.
  for (const card of (message.attachmentCards ?? []) as AttachmentCardItem[]) {
    if (card.kind === 'image') {
      if (card.src && !images.includes(card.src)) images.push(card.src);
      else if (!card.src) add('Image', card.name || 'Image', card.id);
      continue;
    }
    add(card.fileType || 'File', card.name || 'File', card.id);
  }

  // Real uploads. `splitFilename` is the shipping chat's own split, so `deck.pdf` reads as PDF /
  // deck here exactly as it does there.
  for (const file of message.files ?? []) {
    const { type, name } = splitFilename(file.filename);
    add(type, name, `file-${file.url || file.filename}`);
  }

  // The markers, which are the only half of this that survives a reload.
  for (const marker of extractCanvasAttachmentCards(message.content ?? '')) {
    add(marker.fileType, marker.name, marker.id);
  }

  // The artifact-focus marker names a slide the same way, so it earns the same chip: the words are
  // stripped from the bubble, and the chip is what those words looked like to a reader.
  for (const marker of (message.content ?? '').match(ARTIFACT_MARKER) ?? []) {
    const slide = /slide (\d+)/.exec(marker);
    if (slide) add('Slide', `Slide ${slide[1]}`, `focus-${slide[1]}`);
  }

  // A passage the user selected in the goal or the outline before typing. `Goal: the first 28
  // characters…` is the shipping chat's own label for the same chip, so the two surfaces name one
  // object the same way.
  const selection = message.documentTextSelection;
  if (selection) {
    const label = getDocumentSelectionChipLabel(selection);
    const at = label.indexOf(': ');
    add(at === -1 ? 'Doc' : label.slice(0, at), at === -1 ? label : label.slice(at + 2), `sel-${selection.id}`);
  }

  return images.length === 0 && cards.length === 0 ? NO_DECORATIONS : { images, cards };
}

/** What a turn pointed AT, as opposed to what it carried. `ArtifactReference` takes exactly this. */
export interface MessageAnnotations {
  /**
   * Which artifact the pins belong to — the number `annotationsSummary` writes into its own heading,
   * and the only thing in a persisted prompt that says which one.
   *
   * Null for a block written before that line existed. That is a degraded reference and not a
   * missing one: the marks ARE the annotation and the thumbnail is only what they were drawn on, so
   * the block still renders, on empty ground.
   */
  slideId: number | string | null;
  pins: ArtifactReferencePin[];
}

/**
 * What a user turn pointed at, rebuilt from the prompt that outlived the notes.
 *
 * The sibling of `decorationsFromMessage`, against the same failure one layer deeper. Annotations
 * are session-local by design — `annotateBus` never writes them to the blob or to storage, because
 * "make THIS bigger" does not survive a regeneration anyway — so once the tab is gone the only
 * surviving record that a turn pointed at anything is the `[Pointed annotations…]` block in the
 * persisted prompt. And `stripSystemContext` takes that block off the bubble PRECISELY because the
 * server still holds it. Nothing reading it back is the reload keeping "tighten the header" and
 * losing which header.
 *
 * Everything comes off `withAnnotations` rather than being re-derived here. That file writes the
 * block and reads it ten lines apart on purpose, and the mark rule in particular — an area note's
 * pin sits at the centre of its rect — is also drawn live by `AnnotateLayer`; a third copy would
 * drift on the first edit that only remembered two.
 *
 * Null rather than an object with no pins, because "this turn pointed at nothing" and "this turn
 * pointed at an artifact and marked nothing on it" are different pictures and only the second is
 * worth a block. `decorationsFromMessage` gets the opposite treatment (a shared `NO_DECORATIONS`)
 * because its caller wants two lists either way; this one's caller is deciding whether to render.
 */
export function annotationsFromMessage(message: PlanningChatMessage): MessageAnnotations | null {
  if (message.role !== 'user') return null;

  const content = message.content ?? '';
  const notes = parseAnnotationsBlock(content);
  if (notes.length === 0) return null;

  return {
    slideId: annotationsBlockSlideId(content),
    pins: notes.map((note, at): ArtifactReferencePin => ({
      // Keyed on the position in the block, not on `n`. The number is read out of text the parser is
      // deliberately tolerant of, so two notes can arrive wearing the same one — and two React
      // children sharing a key is a worse outcome than a pin drawn with a repeated label.
      id: `${message.id}-pin-${at}`,
      n: note.n,
      // A rect is what makes a note an area one — the same test `annotationName` makes when it calls
      // one `Area selection`, so the pin's shape and the note's own name agree.
      kind: note.w != null && note.h != null ? 'area' : 'point',
      // Spread whole rather than read field by field: the one rule this block must not restate is
      // where a mark goes, and taking both numbers together makes it impossible to keep half of it.
      ...annotationMark(note),
      note: note.note,
    })),
  };
}
